/**
 * PathKit Control Grid — System Admin tab.
 *
 * Read-only view embedded in /system-admin so platform operators can see the
 * same product-health rollup that the standalone /internal/control-grid page
 * shows, without leaving the admin panel. Uses the same `useControlGridData`
 * hook so both surfaces stay in lockstep.
 *
 * Styling matches the rest of SystemAdminPanel (Card chrome, Table, Badge,
 * Alert, light theme), not the dark mono look of the standalone page.
 */

import { useMemo, useState } from 'react';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  ClipboardCheck,
  Clock,
  ExternalLink,
  Play,
  RefreshCw,
} from 'lucide-react';

import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

import { useControlGridData } from '../utils/controlGrid/useControlGridData';
import type {
  CapabilityConfig,
  ControlGridRow,
  RowStatus,
} from '../utils/controlGrid/build';
import { ManualCheckDialog } from './ManualCheckDialog';

const STATUS_LABEL: Record<RowStatus, string> = {
  healthy: 'Healthy',
  watch: 'Watch',
  broken: 'Broken',
  unknown: 'Unknown',
};

function StatusBadge({ status }: { status: RowStatus }) {
  const Icon =
    status === 'healthy'
      ? CheckCircle2
      : status === 'watch'
        ? AlertTriangle
        : status === 'broken'
          ? AlertCircle
          : CircleDashed;

  // Inline class strings (instead of cva variants) so this stays self-contained
  // and matches the rest of the admin panel's Tailwind palette.
  const cls =
    status === 'healthy'
      ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-950 dark:text-green-200 dark:border-green-900'
      : status === 'watch'
        ? 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-950 dark:text-yellow-200 dark:border-yellow-900'
        : status === 'broken'
          ? 'bg-red-100 text-red-800 border-red-200 dark:bg-red-950 dark:text-red-200 dark:border-red-900'
          : 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}
    >
      <Icon className="h-3 w-3" />
      {STATUS_LABEL[status]}
    </span>
  );
}

function ConfidenceBar({ value, status }: { value: number; status: RowStatus }) {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  const fill =
    status === 'healthy'
      ? 'bg-green-500'
      : status === 'watch'
        ? 'bg-yellow-500'
        : status === 'broken'
          ? 'bg-red-500'
          : 'bg-gray-300 dark:bg-gray-600';
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
        <div className={`h-full ${fill}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs tabular-nums text-muted-foreground w-10 text-right">
        {pct}%
      </span>
    </div>
  );
}

function PriorityBadge({ priority }: { priority: ControlGridRow['priority'] }) {
  const variant: 'default' | 'secondary' | 'destructive' | 'outline' =
    priority === 'P0' ? 'destructive' : priority === 'P1' ? 'default' : 'secondary';
  return (
    <Badge variant={variant} className="text-[10px] tabular-nums">
      {priority}
    </Badge>
  );
}

function SourceBadge({ source }: { source: ControlGridRow['source'] }) {
  const label = source.toUpperCase();
  const cls =
    source === 'override'
      ? 'border-purple-300 text-purple-700 dark:text-purple-300'
      : source === 'mixed'
        ? 'border-cyan-300 text-cyan-700 dark:text-cyan-300'
        : source === 'none'
          ? 'border-gray-300 text-muted-foreground'
          : 'border-gray-300 text-foreground/70';
  return (
    <Badge variant="outline" className={`text-[10px] tracking-wider ${cls}`}>
      {label}
    </Badge>
  );
}

function FailedTestsList({ names }: { names: string[] }) {
  if (names.length === 0) return null;
  return (
    <details className="mt-2 text-xs">
      <summary className="cursor-pointer text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 select-none">
        {names.length} failed test{names.length === 1 ? '' : 's'}
      </summary>
      <ul className="mt-1 ml-3 space-y-0.5 text-muted-foreground">
        {names.map((n) => (
          <li key={n} className="font-mono break-all">
            {n}
          </li>
        ))}
      </ul>
    </details>
  );
}

function formatRelative(iso: string | null, now: number): string {
  if (!iso) return 'never';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const diff = now - t;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}

export function ControlGridSystemAdminTab() {
  const now = Date.now();
  const {
    result,
    noRun,
    loading,
    reload,
    running,
    runStatus,
    runTests,
    capabilities,
    overrides,
  } = useControlGridData();

  const counts = useMemo(() => {
    const c: Record<RowStatus, number> = { healthy: 0, watch: 0, broken: 0, unknown: 0 };
    for (const r of result.rows) c[r.status] += 1;
    return c;
  }, [result.rows]);

  // Index capabilities by id so each row can find its manualChecks (if any)
  // without re-scanning the array on every render.
  const capabilityById = useMemo(() => {
    const m = new Map<string, CapabilityConfig>();
    for (const c of capabilities.capabilities) m.set(c.id, c);
    return m;
  }, [capabilities]);

  const [activeManualCapability, setActiveManualCapability] =
    useState<CapabilityConfig | null>(null);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Control Grid
            </CardTitle>
            <CardDescription>
              Live product health by capability — sourced from the latest
              automated test run plus manual overrides.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              {counts.healthy} healthy
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-yellow-500" />
              {counts.watch} watch
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-500" />
              {counts.broken} broken
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-gray-400" />
              {counts.unknown} unknown
            </span>
            <span className="inline-flex items-center gap-1 ml-2">
              <Clock className="h-3 w-3" />
              {formatRelative(result.lastRunAt, now)}
            </span>
            <Button
              type="button"
              size="sm"
              variant="default"
              onClick={runTests}
              disabled={running}
              title="Run npm run test:control-grid via the dev server (local only)"
              className="ml-2 h-7 px-2 text-xs"
            >
              <Play
                className={`h-3 w-3 mr-1 ${running ? 'animate-pulse' : ''}`}
              />
              {running ? 'Running…' : 'Run tests'}
            </Button>
            <button
              type="button"
              onClick={reload}
              disabled={loading || running}
              title="Re-fetch tests/last-run.json without running vitest"
              className="inline-flex items-center gap-1 ml-1 px-2 py-0.5 rounded border border-border hover:border-foreground/40 transition-colors disabled:opacity-50 disabled:cursor-wait"
            >
              <RefreshCw
                className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`}
              />
              {loading ? 'loading…' : 'refresh'}
            </button>
            <a
              href="/internal/control-grid"
              className="inline-flex items-center gap-1 ml-1 px-2 py-0.5 rounded border border-border hover:border-foreground/40 transition-colors"
              title="Open the standalone Control Grid page"
            >
              full view <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {runStatus && runStatus.unavailable && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              In-browser test runs are only available when the app is served
              by{' '}
              <code className="px-1 py-0.5 rounded bg-muted text-xs">
                vite dev
              </code>
              . Run{' '}
              <code className="px-1 py-0.5 rounded bg-muted text-xs">
                npm run test:control-grid
              </code>{' '}
              in a terminal instead.
            </AlertDescription>
          </Alert>
        )}
        {runStatus && !runStatus.unavailable && (
          <Alert variant={runStatus.ok ? 'default' : 'destructive'}>
            {runStatus.ok ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <AlertDescription>
              {runStatus.ok ? (
                <>
                  Test run completed in{' '}
                  {(runStatus.durationMs / 1000).toFixed(1)}s. All
                  test-derived rows now reflect the latest results.
                </>
              ) : (
                <>
                  Test run finished with failures (exit {runStatus.exitCode},{' '}
                  {(runStatus.durationMs / 1000).toFixed(1)}s). The rows below
                  show the actual failing tests.
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Suppressed during initial fetch to avoid flicker. */}
        {noRun && !loading && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <span className="font-semibold">No test run found.</span>{' '}
              Run{' '}
              <code className="px-1 py-0.5 rounded bg-muted text-xs">
                npm run test:control-grid
              </code>{' '}
              to generate{' '}
              <code className="px-1 py-0.5 rounded bg-muted text-xs">
                tests/last-run.json
              </code>
              . Manual overrides still apply; capabilities with no override
              show as <span className="font-medium">Unknown</span>.
            </AlertDescription>
          </Alert>
        )}

        {!noRun && result.staleRun && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <span className="font-semibold">Test run is stale</span> (
              {formatRelative(result.lastRunAt, now)}, older than 24h). All
              test-derived rows are shown as Unknown until you re-run{' '}
              <code className="px-1 py-0.5 rounded bg-muted text-xs">
                npm run test:control-grid
              </code>
              .
            </AlertDescription>
          </Alert>
        )}

        {result.warnings.length > 0 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <span className="font-semibold">
                {result.warnings.length} mapping warning
                {result.warnings.length === 1 ? '' : 's'}:
              </span>
              <ul className="mt-1 ml-4 space-y-0.5 text-xs list-disc">
                {result.warnings.map((w, i) => (
                  <li key={`${w.capabilityId}-${i}`}>
                    <span className="font-medium">{w.capabilityId}</span>{' '}
                    <span className="text-muted-foreground">· {w.reason} ·</span>{' '}
                    <code>{w.mapping.file}</code>
                    {w.mapping.describe && <code> › {w.mapping.describe}</code>}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        <div className="rounded-md border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Capability</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Confidence</TableHead>
                <TableHead className="whitespace-nowrap">
                  Tests p / f / s / total
                </TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Next action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.rows.map((row) => {
                const cap = capabilityById.get(row.id);
                const hasManualChecks =
                  !!cap?.manualChecks && cap.manualChecks.length > 0;
                return (
                <TableRow key={row.id}>
                  <TableCell className="align-top">
                    <div className="font-medium">{row.label}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      {row.id}
                    </div>
                    {row.notes && (
                      <div className="text-xs text-muted-foreground mt-1 max-w-md">
                        {row.notes}
                      </div>
                    )}
                    {hasManualChecks && cap && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2 h-6 text-[11px] px-2"
                        onClick={() => setActiveManualCapability(cap)}
                      >
                        <ClipboardCheck className="h-3 w-3 mr-1" />
                        Run manual checks ({cap.manualChecks!.length})
                      </Button>
                    )}
                  </TableCell>
                  <TableCell className="align-top">
                    <StatusBadge status={row.status} />
                  </TableCell>
                  <TableCell className="align-top">
                    <ConfidenceBar value={row.confidence} status={row.status} />
                  </TableCell>
                  <TableCell className="align-top whitespace-nowrap tabular-nums">
                    <div>
                      <span className="text-green-600 dark:text-green-400">
                        {row.testsPassed}
                      </span>
                      <span className="text-muted-foreground"> / </span>
                      <span
                        className={
                          row.testsFailed > 0
                            ? 'text-red-600 dark:text-red-400 font-semibold'
                            : 'text-muted-foreground'
                        }
                      >
                        {row.testsFailed}
                      </span>
                      <span className="text-muted-foreground"> / </span>
                      <span className="text-muted-foreground">
                        {row.testsSkipped}
                      </span>
                      <span className="text-muted-foreground"> / </span>
                      <span>{row.testsTotal}</span>
                    </div>
                    <FailedTestsList names={row.failedTestNames} />
                  </TableCell>
                  <TableCell className="align-top">
                    <SourceBadge source={row.source} />
                  </TableCell>
                  <TableCell className="align-top">
                    <PriorityBadge priority={row.priority} />
                  </TableCell>
                  <TableCell className="align-top max-w-sm">
                    {row.nextAction ? (
                      <span className="text-sm">{row.nextAction}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <p className="text-xs text-muted-foreground">
          {result.rows.length} capabilities · sourced from{' '}
          <code>internal/control-grid/capabilities.json</code> · re-run{' '}
          <code>npm run test:control-grid</code> to refresh.
        </p>
      </CardContent>

      {activeManualCapability && (
        <ManualCheckDialog
          open={!!activeManualCapability}
          onOpenChange={(open) => {
            if (!open) setActiveManualCapability(null);
          }}
          capability={activeManualCapability}
          currentOverridesFile={overrides}
          thresholds={capabilities.thresholds}
        />
      )}
    </Card>
  );
}
